import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Mail, Phone, Calendar, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/messages`);
      setMessages(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/messages/${id}/status?status=${status}`);
      toast.success('Statut mis à jour');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Supprimer définitivement le message de ${m.name} ?\nCette action est irréversible.`)) return;
    try {
      await axios.delete(`${API}/admin/messages/${m._id}`);
      toast.success('Message supprimé');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Messages de contact</h1>
        <p className="text-gray-500 mt-1">{messages.length} message(s)</p>
      </div>

      {loading ? (
        <div className="text-gray-500">Chargement...</div>
      ) : messages.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-500">
          Aucun message pour le moment.
        </div>
      ) : (
        <div className="space-y-4" data-testid="admin-messages-list">
          {messages.map((m) => (
            <div
              key={m._id}
              className={`bg-white rounded-lg border p-6 transition-all ${
                m.status === 'new' ? 'border-[#E42313] shadow-sm' : 'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-semibold text-gray-900">{m.subject}</h3>
                    <span
                      className={`text-xs px-2 py-0.5 rounded font-medium ${
                        m.status === 'new'
                          ? 'bg-red-100 text-red-700'
                          : m.status === 'read'
                            ? 'bg-gray-100 text-gray-600'
                            : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {m.status === 'new' ? 'Nouveau' : m.status === 'read' ? 'Lu' : 'Répondu'}
                    </span>
                  </div>
                  <div className="text-gray-800 font-medium">{m.name}</div>
                </div>
                <div className="flex items-center gap-2">
                <select
                  value={m.status}
                  onChange={(e) => setStatus(m._id, e.target.value)}
                  className="text-xs px-3 py-1.5 border rounded bg-white"
                >
                  <option value="new">Nouveau</option>
                  <option value="read">Lu</option>
                  <option value="replied">Répondu</option>
                </select>
                <button
                  onClick={() => remove(m)}
                  className="ml-2 p-2 text-gray-500 hover:text-red-600 inline-flex"
                  title="Supprimer le message"
                  data-testid={`delete-message-${m._id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                </div>
              </div>

              <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-4">
                <a
                  href={`mailto:${m.email}`}
                  className="flex items-center gap-1 hover:text-[#E42313]"
                >
                  <Mail className="w-4 h-4" /> {m.email}
                </a>
                {m.phone && (
                  <a
                    href={`tel:${m.phone}`}
                    className="flex items-center gap-1 hover:text-[#E42313]"
                  >
                    <Phone className="w-4 h-4" /> {m.phone}
                  </a>
                )}
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" /> {new Date(m.createdAt).toLocaleString('fr-FR')}
                </span>
              </div>

              <div className="bg-gray-50 border-l-4 border-[#E42313] p-4 rounded whitespace-pre-wrap text-gray-700">
                {m.message}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminMessages;
